import { createContext, useContext } from 'react';
import {
  TApiApplicationDocument,
  TApplicationStatus,
  TLocalFileEntry,
  TSignatureValidationState,
} from '~/modules/covenants/types.ts';
import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';

export interface IDocListContextValue {
  contragentId: number;
  applicationStatus: TApplicationStatus;
  isNewApplication: boolean;
  showDeadlineColumn: boolean;
  localFiles: Record<string, TLocalFileEntry>;
  signatureValidationState: TSignatureValidationState;
  isReadOnly: boolean;
  onUploadForDoc: (docId: string) => void;
  onUploadSignatureClick: (docId: string, doc?: TApiApplicationDocument) => void;
  onDeleteDocument: (docId: string) => void;
  onDeleteSignature: (docId: string, doc?: TApiApplicationDocument) => void;
  onPreviewDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onDownloadDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onAddDocumentRow: (doc: TApiApplicationDocument) => void;
  onSaveFinancialCondition: (values: TFinancialConditionFormValues) => Promise<boolean>;
  onLoadFinancialCondition: (
    doc: TApiApplicationDocument
  ) => Promise<TFinancialConditionCertificateResponse | null>;
  onSaveIncomeStructureByKved: (
    doc: TApiApplicationDocument,
    values: TIncomeStructureByKvedFormValues
  ) => Promise<boolean>;
  onLoadIncomeStructureByKved: (
    doc: TApiApplicationDocument
  ) => Promise<TIncomeStructureByKvedCertificateResponse | null>;
  onGenerateDocument: (doc: TApiApplicationDocument, isFop?: boolean) => Promise<boolean>;
}

const DocListContext = createContext<IDocListContextValue | null>(null);

export const DocListProvider = DocListContext.Provider;

export const useDocListContext = () => {
  const context = useContext(DocListContext);

  if (!context) {
    throw new Error('useDocListContext must be used within DocListProvider');
  }

  return context;
};
